// =============================================================================
// ComposeModal — "+ Compose" entry point. Two tabs:
//   1. New direct message — search teammates by display name → openDm + open
//   2. New team — name + icon + accent → createChannel + open
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import { getSupabase } from "../client.js";
import { openDm, createChannel } from "../send.js";
import { SevricMark } from "./SevricMark.jsx";

const ICONS = [
  { key: "people", label: "Team" },
  { key: "wrench", label: "Service" },
  { key: "home", label: "Floor" },
  { key: "sun", label: "Shift" },
  { key: "star", label: "Special" },
];
const ACCENTS = [
  { key: "cobalt", color: "#2E5694", soft: "rgba(46,86,148,0.10)" },
  { key: "amber", color: "#B05E13", soft: "rgba(176,94,19,0.10)" },
  { key: "green", color: "#2A6D38", soft: "rgba(42,109,56,0.10)" },
  { key: "gold", color: "#946A12", soft: "rgba(148,106,18,0.12)" },
  { key: "burgundy", color: "#7C2935", soft: "rgba(124,41,53,0.10)" },
];

export function ComposeModal({ profile, onClose, onOpenScope }) {
  const [tab, setTab] = useState("dm"); // 'dm' | 'team'
  return (
    <div className="chat-archive-modal open" onClick={onClose}>
      <div className="chat-archive-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="chat-archive-close" onClick={onClose} aria-label="Close">×</button>
        <div className="chat-archive-h1">Start a <em>conversation</em>.</div>
        <div className="chat-archive-sub">
          Open a direct message with a teammate, or create a new team channel.
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          <button type="button" className={`chat-tab${tab === "dm" ? " active" : ""}`} onClick={() => setTab("dm")}>Direct message</button>
          <button type="button" className={`chat-tab${tab === "team" ? " active" : ""}`} onClick={() => setTab("team")}>New team</button>
        </div>
        {tab === "dm"
          ? <DmTab profile={profile} onOpenScope={onOpenScope} onClose={onClose} />
          : <TeamTab profile={profile} onOpenScope={onOpenScope} onClose={onClose} />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DM tab — search sevric_users by display_name within the caller's tenant.
// SEVRIC AI is always at the top of results, never filtered out.
// -----------------------------------------------------------------------------
function DmTab({ profile, onOpenScope, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const supa = getSupabase();
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let q = supa.from("sevric_users")
          .select("uid, display_name, initials, presence, presence_label")
          .neq("uid", profile.uid)
          .neq("tenant_id", "__deleted__")
          .limit(40);
        if (query.trim()) q = q.ilike("display_name", `%${query.trim()}%`);
        const res = await q;
        if (cancelled) return;
        // Always show SEVRIC AI first.
        const ai = (res.data || []).find((u) => u.uid === "sevric-ai");
        const humans = (res.data || []).filter((u) => u.uid !== "sevric-ai")
          .sort((a, b) => (a.display_name || "").localeCompare(b.display_name || ""));
        setResults([ai, ...humans].filter(Boolean));
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [query, profile.uid]);

  async function openConversation(other) {
    setBusy(other.uid);
    try {
      const dm = await openDm({ tenantId: profile.tenantId, callerUid: profile.uid, otherUid: other.uid });
      onOpenScope({ kind: "dm", id: dm.id, _meta: { isAi: other.uid === "sevric-ai" } });
      onClose();
    } catch {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="chat-archive-search-wrap">
        <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          className="chat-archive-search"
          placeholder="Search teammates by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {loading && <div style={{ padding: 22, color: "var(--text-dim, #6A6D74)" }}>Loading teammates…</div>}
      {!loading && results.length === 0 && (
        <div style={{ padding: 22, color: "var(--text-muted, #4A4D54)" }}>
          {query ? "Nobody by that name." : "No teammates in your tenant yet."}
        </div>
      )}
      {!loading && results.map((u) => {
        const isAi = u.uid === "sevric-ai";
        const initials = (u.initials || u.display_name || "??").slice(0, 2).toUpperCase();
        return (
          <div key={u.uid} className="chat-archive-row" onClick={() => openConversation(u)} style={{ cursor: "pointer" }}>
            <div className={`chat-compose-avi${isAi ? " ai" : ""}`}>
              {isAi ? <SevricMark size={34} /> : initials}
            </div>
            <div>
              <div className="chat-archive-name">{isAi ? "SEVRIC AI" : (u.display_name || u.uid)}</div>
              <div className="chat-archive-sub2">{isAi ? "Always on · knows your floor" : (u.presence_label || u.presence || "—")}</div>
            </div>
            <div></div>
            <button type="button" className="chat-archive-restore" disabled={busy === u.uid}>
              {busy === u.uid ? "Opening…" : "Message →"}
            </button>
          </div>
        );
      })}
    </>
  );
}

// -----------------------------------------------------------------------------
// Team tab — name + icon + accent. Caller becomes admin via createChannel.
// -----------------------------------------------------------------------------
function TeamTab({ profile, onOpenScope, onClose }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("people");
  const [accent, setAccent] = useState("cobalt");
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const slug = useMemo(() => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48), [name]);

  async function submit() {
    if (!name.trim()) { setError("Pick a name first."); return; }
    setBusy(true);
    setError(null);
    try {
      const channel = await createChannel({
        tenantId: profile.tenantId,
        createdBy: profile.uid,
        name: name.trim(),
        description: description.trim() || null,
        icon, accent, isPrivate,
      });
      onOpenScope({ kind: "channel", id: channel.id });
      onClose();
    } catch (err) {
      setError(err?.message || "Couldn't create the team.");
      setBusy(false);
    }
  }

  const accentObj = ACCENTS.find((a) => a.key === accent) || ACCENTS[0];

  return (
    <>
      <label className="chat-field-label">Team name</label>
      <input
        type="text"
        className="chat-archive-search"
        placeholder="e.g., Porter Team"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={48}
        autoFocus
      />
      {slug && <div style={{ fontSize: 11.5, color: "var(--text-dim, #6A6D74)", marginTop: 4, marginBottom: 12 }}>/teams/{slug}</div>}

      <label className="chat-field-label">Description <span style={{ color: "var(--text-dim, #6A6D74)", fontWeight: 400, fontSize: 11 }}>(optional)</span></label>
      <input
        type="text"
        className="chat-archive-search"
        placeholder="What does this team talk about?"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={120}
      />

      <label className="chat-field-label" style={{ marginTop: 16 }}>Icon</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {ICONS.map((i) => (
          <button
            key={i.key}
            type="button"
            onClick={() => setIcon(i.key)}
            className={`chat-icon-chip${icon === i.key ? " active" : ""}`}
            style={icon === i.key ? { background: accentObj.soft, color: accentObj.color, borderColor: accentObj.color } : undefined}
          >
            {i.label}
          </button>
        ))}
      </div>

      <label className="chat-field-label">Accent</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {ACCENTS.map((a) => (
          <button
            key={a.key}
            type="button"
            aria-label={a.key}
            onClick={() => setAccent(a.key)}
            className={`chat-accent-chip${accent === a.key ? " active" : ""}`}
            style={{ background: a.color, boxShadow: accent === a.key ? `0 0 0 3px ${a.soft}` : "none" }}
          />
        ))}
      </div>

      <label className="chat-field-label" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 14 }}>
        <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
        Private team — invite-only
      </label>

      {error && (
        <div style={{ padding: "8px 12px", marginBottom: 12, background: "rgba(124,41,53,0.06)", border: "1px solid rgba(124,41,53,0.22)", borderRadius: 8, color: "#7C2935", fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="chat-archive-restore" onClick={onClose}>Cancel</button>
        <button type="button" className="chat-msg-btn" onClick={submit} disabled={busy || !name.trim()}>
          {busy ? "Creating…" : "Create team →"}
        </button>
      </div>
    </>
  );
}
